import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_NEAR_BOTTOM_PX = 100;

type UseLoadMoreOnScrollOptions = {
  hasMore: boolean;
  isLoading?: boolean;
  onLoadMore: () => void | Promise<void>;
  nearBottomPx?: number;
  itemCount?: number;
  /** When true, fetches more on mount if the list is shorter than the scroll container. Default false. */
  autoLoadOnMount?: boolean;
};

export function useLoadMoreOnScroll({
  hasMore,
  isLoading = false,
  onLoadMore,
  nearBottomPx = DEFAULT_NEAR_BOTTOM_PX,
  itemCount = 0,
  autoLoadOnMount = false,
}: UseLoadMoreOnScrollOptions) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingMore = useRef(false);

  const maybeLoadMoreNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasMore || isLoading || loadingMore.current) {
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= nearBottomPx) {
      loadingMore.current = true;
      void Promise.resolve(onLoadMore()).finally(() => {
        loadingMore.current = false;
      });
    }
  }, [hasMore, isLoading, nearBottomPx, onLoadMore]);

  const onScroll = useCallback(() => {
    maybeLoadMoreNearBottom();
  }, [maybeLoadMoreNearBottom]);

  useEffect(() => {
    if (!autoLoadOnMount || isLoading) {
      return;
    }
    maybeLoadMoreNearBottom();
  }, [autoLoadOnMount, isLoading, hasMore, itemCount, maybeLoadMoreNearBottom]);

  return { scrollRef, onScroll, maybeLoadMoreNearBottom };
}
